'use client';

import { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import {
  consumeAssistantArtifactHandoff,
  type AssistantArtifactDestination,
} from '@/app/lib/assistantArtifactHandoff';

export const ASSISTANT_ARTIFACT_DESTINATION_FOR_PATH: Record<string, AssistantArtifactDestination> = {
  '/scheduling': 'scheduling',
  '/degree-planning': 'degree_planning',
  '/explore': 'explore',
  '/classes-on-map': 'maps',
  '/course-sniper': 'course_sniper',
};

export function assistantArtifactDraftFacts(destination: AssistantArtifactDestination, draft: Record<string, unknown>) {
  switch (destination) {
    case 'scheduling': {
      const classIds = Array.isArray(draft.class_ids) ? draft.class_ids.filter((id): id is string => typeof id === 'string') : [];
      return classIds.length ? [{ label: 'Section candidates', value: classIds.join(', ') }] : [];
    }
    case 'degree_planning': return typeof draft.degree_id === 'number' ? [{ label: 'Proposed degree', value: `Degree #${draft.degree_id}` }] : [];
    case 'explore': return typeof draft.course_id === 'string' ? [{ label: 'Course to inspect', value: draft.course_id }] : [];
    case 'maps': return typeof draft.building_code === 'string' ? [{ label: 'Building to focus', value: draft.building_code }] : [];
    case 'course_sniper': return typeof draft.section_id === 'string' ? [{ label: 'Section to review', value: draft.section_id }] : [];
  }
}

export function assistantArtifactReviewAction(destination: AssistantArtifactDestination, draft: Record<string, unknown>) {
  switch (destination) {
    // `importDegreeId` is intentionally not used here: the existing planner
    // import flow can persist to a selected plan. Assistant handoffs must stay
    // review-only until the student starts a separate planner action.
    case 'degree_planning': return null;
    case 'explore': return typeof draft.course_id === 'string' ? { label: 'Inspect course', query: { course: draft.course_id } } : null;
    case 'maps': return typeof draft.building_code === 'string' ? { label: 'Focus building on map', query: { building: draft.building_code } } : null;
    case 'course_sniper': return typeof draft.section_id === 'string' ? { label: 'Review section watch', query: { course: draft.section_id } } : null;
    case 'scheduling': return null;
  }
}

/**
 * Consumes an opaque assistant handoff exactly once on its destination route.
 * It intentionally displays a review state only: none of these facts are
 * persisted, submitted, or converted into a protected action.
 */
export function AssistantArtifactHandoffNotice() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [notice, setNotice] = useState<{ destination: AssistantArtifactDestination; draft: Record<string, unknown> } | null>(null);
  const [copied, setCopied] = useState(false);
  const handoffId = searchParams.get('assistantArtifact');
  const destination = ASSISTANT_ARTIFACT_DESTINATION_FOR_PATH[pathname];

  useEffect(() => {
    if (!handoffId || !destination) return;
    const draft = consumeAssistantArtifactHandoff(destination, handoffId);
    if (draft) setNotice({ destination, draft });

    const next = new URLSearchParams(searchParams.toString());
    next.delete('assistantArtifact');
    router.replace(`${pathname}${next.size ? `?${next}` : ''}`, { scroll: false });
  }, [destination, handoffId, pathname, router, searchParams]);

  if (!notice) return null;
  const facts = assistantArtifactDraftFacts(notice.destination, notice.draft);
  const action = assistantArtifactReviewAction(notice.destination, notice.draft);
  const applyReviewAction = () => {
    if (!action) return;
    const next = new URLSearchParams(searchParams.toString());
    next.delete('assistantArtifact');
    Object.entries(action.query).forEach(([key, value]) => next.set(key, value));
    router.push(`${pathname}?${next}`);
  };
  const copyCandidates = async () => {
    const candidates = facts[0]?.value;
    if (!candidates || !navigator.clipboard) return;
    await navigator.clipboard.writeText(candidates);
    setCopied(true);
  };
  return (
    <aside className="mx-auto mt-3 w-[min(100%-2rem,72rem)] rounded-card border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-e1 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200" aria-label="Assistant draft ready for review">
      <p className="font-semibold text-slate-900 dark:text-white">Assistant draft ready</p>
      <dl className="mt-1 space-y-1 text-slate-600 dark:text-slate-300">
        {facts.map((fact) => <div key={fact.label}><dt className="inline font-medium text-slate-800 dark:text-slate-100">{fact.label}: </dt><dd className="inline break-all">{fact.value}</dd></div>)}
      </dl>
      <p className="mt-1 text-slate-600 dark:text-slate-300">Review before making any changes; this draft has not been saved, submitted, or started as a watch.</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {action ? <button type="button" onClick={applyReviewAction} className="min-h-12 rounded-control border border-slate-300 px-3 text-xs font-semibold text-slate-800 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800">{action.label}</button> : null}
        {notice.destination === 'scheduling' ? <button type="button" onClick={() => void copyCandidates()} className="min-h-12 rounded-control border border-slate-300 px-3 text-xs font-semibold text-slate-800 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 dark:border-slate-600 dark:text-slate-100 dark:hover:bg-slate-800">{copied ? 'Section candidates copied' : 'Copy section candidates'}</button> : null}
      </div>
    </aside>
  );
}
